import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Store, Palette, FileText, DollarSign, Upload, X, Image } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const MarketplacePage = () => {
  const [showWarning, setShowWarning] = useState(true);
  const [totalSales, setTotalSales] = useState(0);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [signedFiles, setSignedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const location = useLocation();

  // Reset to warning screen when navigating to this page
  useEffect(() => {
    setShowWarning(true);
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, [location.key]);

  // Fetch total sales
  useEffect(() => {
    const fetchSales = async () => {
      try {
        const result = await api.getSalesTotal();
        setTotalSales(result.total || 0);
      } catch (error) {
        console.error('Error fetching sales:', error);
      }
    };
    fetchSales();
  }, []);

  // Fetch marketplace products when entering marketplace
  useEffect(() => {
    if (!showWarning) {
      const fetchProducts = async () => {
        setLoading(true);
        try {
          const data = await api.getMarketplaceProducts();
          setProducts(data);
        } catch (error) {
          console.error('Error fetching marketplace products:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchProducts();
    }
  }, [showWarning]);

  if (showWarning) {
    return (
      <div data-testid="marketplace-warning" className="pt-20 min-h-screen bg-void flex items-center justify-center px-6">
        <div className="max-w-3xl w-full text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/jmls7wl2_artist%20warning.png"
            alt="Only Skullz Artist Marketplace Warning"
            className="w-full max-w-2xl mx-auto mb-12"
          />

          <div className="grid grid-cols-2 gap-4 md:gap-6">
            {/* Browse Marketplace */}
            <button
              onClick={() => setShowWarning(false)}
              data-testid="browse-marketplace-btn"
              className="p-4 md:p-8 border border-white/10 bg-void-paper hover:border-cyan-400/50 transition-all duration-300 group text-center"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-cyan-400/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-cyan-400/30 transition-colors">
                <Store className="w-6 h-6 md:w-8 md:h-8 text-cyan-400" />
              </div>
              <h3 className="font-heading text-base md:text-xl font-bold text-white mb-2 leading-tight">
                Browse<br className="md:hidden" /> Marketplace
              </h3>
              <p className="text-neutral-400 text-xs md:text-sm">
                Explore unique skull art from various artists
              </p>
            </button>

            {/* Artist Application */}
            <a
              href="#artist-application"
              onClick={(e) => {
                e.preventDefault();
                setShowWarning(false);
                setTimeout(() => {
                  document.getElementById('artist-application')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              data-testid="artist-application-btn"
              className="p-4 md:p-8 border border-white/10 bg-void-paper hover:border-cyan-400/50 transition-all duration-300 group text-center"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 bg-cyan-400/20 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-cyan-400/30 transition-colors">
                <Palette className="w-6 h-6 md:w-8 md:h-8 text-cyan-400" />
              </div>
              <h3 className="font-heading text-base md:text-xl font-bold text-white mb-2 leading-tight">
                Artist<br className="md:hidden" /> Application
              </h3>
              <p className="text-neutral-400 text-xs md:text-sm">
                Apply to sell your art on our platform
              </p>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="marketplace-page" className="pt-20 min-h-screen bg-void">
      {/* Marketplace Header */}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
            Community
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter uppercase text-white">
            Marketplace
          </h1>
          <p className="text-neutral-400 mt-4 max-w-xl">
            Discover unique skull art from talented artists in our community.
          </p>
          <div className="mt-6 inline-flex items-center gap-3 bg-cyan-400/10 border border-cyan-400/30 px-6 py-3 rounded">
            <DollarSign className="w-5 h-5 text-cyan-400" />
            <span className="text-sm font-mono text-white">
              <span className="text-cyan-400 font-bold">${totalSales.toLocaleString()}</span> in marketplace sales
            </span>
          </div>
        </div>
      </div>

      {/* Marketplace Products */}
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-pulse text-cyan-400 font-mono">Loading products...</div>
            </div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/marketplace/product/${product.id}`}
                  data-testid={`marketplace-product-${product.id}`}
                  className="group bg-void-paper border border-white/10 overflow-hidden hover:border-cyan-400/50 transition-all duration-300 relative"
                >
                  {/* SAMPLE Stamp - only show if product is marked as sample */}
                  {product.is_sample && (
                    <div className="absolute top-4 left-4 z-20 -rotate-12">
                      <div className="bg-red-600 border-4 border-red-800 px-4 py-1 shadow-lg">
                        <span className="text-white font-mono text-sm font-bold tracking-widest uppercase">Sample</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Sold Out Badge Overlay */}
                  {product.stock <= 0 && (
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-2 bg-black/80 border border-red-500/50 px-3 py-2 rounded">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/xvcz8xtj_Skull%20Logo.jpeg" 
                        alt="Skull" 
                        className="w-5 h-5 rounded-full"
                      />
                      <span className="text-red-500 font-mono text-xs uppercase font-bold tracking-wider">Sold Out</span>
                    </div>
                  )}
                  <div className={`aspect-square overflow-hidden ${product.stock <= 0 ? 'opacity-60' : ''}`}>
                    <img
                      src={product.images[0]?.url}
                      alt={product.images[0]?.alt || product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-bold text-white group-hover:text-cyan-400 transition-colors mb-2 line-clamp-1">
                      {product.title}
                    </h3>
                    <p className="text-neutral-400 text-sm mb-3 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-cyan-400 font-mono font-bold">
                        {product.price > 0 ? `$${product.price.toFixed(2)}` : 'FREE'}
                      </span>
                      {product.stock > 0 ? (
                        <span className="text-xs font-mono uppercase text-green-400">
                          In Stock
                        </span>
                      ) : (
                        <span className="text-xs font-mono uppercase text-red-500 font-bold">
                          Sold Out
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <img
                src="https://customer-assets.emergentagent.com/job_141950ea-d755-47d0-b462-4d0e792f15d3/artifacts/xvcz8xtj_Skull%20Logo.jpeg"
                alt="Only Skullz"
                className="w-24 h-24 mx-auto mb-8 rounded-full"
              />
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-white mb-4">
                No Products Yet
              </h2>
              <p className="text-neutral-400 mb-8">
                Check back soon to discover unique skull art from talented artists.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Artist Application Section */}
      <div id="artist-application" className="px-6 md:px-12 lg:px-24 py-24 bg-void-paper">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
              Join Us
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
              Artist Application
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto mb-6">
              Are you a skull artist looking to reach more collectors? 
              Apply to join our marketplace and showcase your work alongside Only Skullz LLC.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <a
                href="https://customer-assets.emergentagent.com/job_skullz-store/artifacts/44a3tjvt_Artist_Agreement_LOCKED.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-mono text-sm uppercase tracking-widest transition-colors"
              >
                <FileText className="w-4 h-4" />
                Artist Agreement
              </a>
              <a
                href="https://customer-assets.emergentagent.com/job_skullz-store/artifacts/am0ptzsr_Agreement_Signature_Page_FILLABLE.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-mono text-sm uppercase tracking-widest transition-colors"
              >
                <FileText className="w-4 h-4" />
                Signature Page
              </a>
              <a
                href="https://customer-assets.emergentagent.com/job_skullz-store/artifacts/7z01547g_Shipping%20Compliance%20Form.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-mono text-sm uppercase tracking-widest transition-colors"
              >
                <FileText className="w-4 h-4" />
                Shipping Form
              </a>
            </div>
          </div>

          <div className="bg-void border border-white/10 p-8 md:p-12">
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    data-testid="artist-name"
                    placeholder="Your name"
                    className="w-full bg-void-paper border border-white/10 h-14 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    data-testid="artist-email"
                    placeholder="your@email.com"
                    className="w-full bg-void-paper border border-white/10 h-14 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                  Website / Social Media
                </label>
                <input
                  type="text"
                  data-testid="artist-website"
                  placeholder="Link to your work"
                  className="w-full bg-void-paper border border-white/10 h-14 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                  Tell Us About Your Art *
                </label>
                <textarea
                  data-testid="artist-about"
                  placeholder="Describe your style, materials, and what makes your skull art unique..."
                  rows={5}
                  className="w-full bg-void-paper border border-white/10 p-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors resize-none"
                />
              </div>

              {/* Photo Upload Section */}
              <div>
                <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                  Photos of Your Work *
                </label>
                <p className="text-neutral-500 text-xs mb-4">Upload 1-3 photos showcasing your best skull art pieces</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {uploadedImages.map((img, index) => (
                    <div key={img || `upload-${index}`} className="relative group aspect-square bg-void-paper border border-white/10">
                      <img src={img} alt={`Artwork ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setUploadedImages(prev => prev.filter((_, i) => i !== index))}
                        className="absolute top-2 right-2 p-1 bg-black/70 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  {uploadedImages.length < 3 && (
                    <label className="aspect-square bg-void-paper border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-cyan-400/50 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          const remaining = 3 - uploadedImages.length;
                          const toUpload = files.slice(0, remaining);
                          
                          setUploading(true);
                          for (const file of toUpload) {
                            try {
                              const result = await api.uploadImage(file);
                              const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
                              setUploadedImages(prev => [...prev, fullUrl]);
                            } catch (error) {
                              toast.error(`Failed to upload ${file.name}`);
                            }
                          }
                          setUploading(false);
                          e.target.value = '';
                        }}
                      />
                      {uploading ? (
                        <div className="animate-spin w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-neutral-500 mb-2" />
                          <span className="text-neutral-500 text-xs">Add Photos</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
                
                {uploadedImages.length > 0 && (
                  <p className="text-neutral-500 text-xs">{uploadedImages.length}/3 photos uploaded</p>
                )}
              </div>

              {/* Document Upload Section */}
              <div>
                <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                  Upload Documents *
                </label>
                <p className="text-neutral-500 text-xs mb-4">Upload your signed Signature Page and Shipping Form (PDF or image)</p>
                
                <div className="space-y-3">
                  {signedFiles.map((file, index) => (
                    <div key={file.name || `file-${index}`} className="flex items-center gap-3 bg-void-paper border border-white/10 p-3">
                      <FileText className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                      <span className="text-white text-sm truncate flex-1">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setSignedFiles(prev => prev.filter((_, i) => i !== index))}
                        className="p-1 text-red-400 hover:text-red-300 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  
                  {signedFiles.length < 2 && (
                    <label className="flex items-center justify-center gap-3 bg-void-paper border border-dashed border-white/20 p-4 cursor-pointer hover:border-cyan-400/50 transition-colors">
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          setUploadingFiles(true);
                          try {
                            const result = await api.uploadImage(file);
                            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
                            setSignedFiles(prev => [...prev, { name: file.name, url: fullUrl }]);
                          } catch (error) {
                            toast.error(`Failed to upload ${file.name}`);
                          }
                          setUploadingFiles(false);
                          e.target.value = '';
                        }}
                      />
                      {uploadingFiles ? (
                        <div className="animate-spin w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-neutral-500" />
                          <span className="text-neutral-500 text-sm">Upload Documents</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>

              <button
                type="submit"
                data-testid="artist-submit-btn"
                className="w-full md:w-auto bg-cyan-400 text-black px-12 py-4 font-mono uppercase tracking-widest text-sm hover:bg-cyan-300 transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,211,238,0.4)]"
              >
                Submit Application
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketplacePage;
